import { FC, useState, useEffect, useCallback, ChangeEvent } from 'react';
import { debounce } from 'throttle-debounce';
// store
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { getWorkflows } from 'app/store/AutomatedWorkflows/AutomatedWorkflows.async';
// Actions
import { AutomatedWorkflowsActions } from 'app/store/AutomatedWorkflows/AutomatedWorkflows.slice';
// Selectors
import { selectFilter, selectInstantWorkflows } from 'app/store/AutomatedWorkflows/AutomatedWorkflows.selectors';
// Mui
import { Box, Toolbar, Typography } from '@mui/material';
// Components
import DataLoading from 'app/components/DataLoading';
import { Input } from 'app/components/Mui';
import WorkflowItem from './WorkflowItem';
import WorkflowsSorting from 'app/components/WorkflowsSorting';
// Constants
import { SIDEBAR_WIDTH } from 'app/App.constants';
// i18next
import { useTranslation } from 'react-i18next';

const WorkflowsSidebar:FC = () => {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // Selectors
  const workflows = useAppSelector(selectInstantWorkflows);
  const filter = useAppSelector(selectFilter);
  const [ stateFilter, setStateFilter ] = useState(filter);

  // eslint-disable-next-line
  const debounceFilter = useCallback(debounce(500, (field:keyof typeof filter, value:any) => {
    dispatch(AutomatedWorkflowsActions.setFilter({ field, value }))
  }), []);

  const handleChange = (event:ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setStateFilter((prevState) => ({
      ...prevState,
      [name]: value
    }));
    debounceFilter(name as keyof typeof filter, value);
  }

  const handleClearFilter = () => {
    dispatch(AutomatedWorkflowsActions.setInitialField('filter'));
  }

  useEffect(() => {
    setStateFilter(filter);
  }, [filter]);

  useEffect(() => {
    dispatch(getWorkflows({}));

    return () => {
      handleClearFilter();
    }
    // eslint-disable-next-line
  }, []);

  return (
    <Box sx={{
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      width: SIDEBAR_WIDTH,
      bgcolor: 'white',
      borderRight: '1px solid rgba(0,0,0,0.08)',
      overflowY: 'auto'
    }}>
      <Toolbar>
        <Typography variant="h6">{t('pages.workflows.title')}</Typography>
      </Toolbar>
      <Box sx={{ mt: 4, px: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Input
          label={t('labels.search')} id="search" name="search"
          value={stateFilter.search}
          onChange={handleChange}
          size="small"
          margin="none"
          onClear={handleClearFilter}
        />
        <WorkflowsSorting />
      </Box>
      <DataLoading
        data={workflows}
        text={t('pages.workflows.noData')}
      >
        {(data) => (
          <Box sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            overflowY: 'auto',
            p: 4
          }}>
            {data.map((workflow) => (
              <WorkflowItem key={`workflow item ${workflow.id}`} workflow={workflow} />
            ))}
          </Box>
        )}
      </DataLoading>
    </Box>
  )
}

export default WorkflowsSidebar